/**
 * This module exports functions that transform text, specifically facet values. Multiple transforms can
 * be applied to a single bit of text, and will result in valid HTML.
 */

import partialParse from 'partial-json-parser';
import cloneDeep from 'lodash/cloneDeep';
import escape from 'lodash/escape';
import isString from 'lodash/isString';
import isNumber from 'lodash/isNumber';
import isBoolean from 'lodash/isBoolean';
import isArray from 'lodash/isArray';
import isObjectLike from 'lodash/isObjectLike';
import isNull from 'lodash/isNull';

import * as K from 'common/constants';
import { LinkConfig, MarkupRange, TRANSFORM_TYPE, TransformArgs } from 'common/types';
import { Expression, EXPRESSION_TYPE, PARSE_TYPE, Term, TOKEN_TYPE } from 'common/util/filter/types';
import { looksLikeJson, quote, regExEscape, stripDollar, unquote } from 'common/util/string';
import { findTerms } from 'common/util/filter/parse';
import { getMaxLength } from 'common/util/array';

/**
 * Given a field and its value, try to determine if it matched the filter. If it did, put the matching portion in
 * a SPAN with the "matched" class.
 *
 * This may not handle all possible filters. Any term that matches will be highlighted regardless of whether it's
 * part of a boolean expression with AND or OR. That should be fine. We don't have to worry about negated terms
 * because they won't be part of the result set.
 *
 * @param field         facet name
 * @param text          value of the facet
 * @param args          args for transform (will have filter)
 *
 * @return list of MarkupRange with info about changes to make
 */
export const showMatches = (field: string, text: string, args?: TransformArgs): MarkupRange[] => {
  const filter = args?.filter;
  if (!text || !filter) {
    return [];
  }

  // used for noting which text matched
  const ranges: MarkupRange[] = [];
  const MAX_RANGES = 100;
  const startTag = '<span class="matched">';
  const endTag = '</span>';

  // add highlight SPANs to the text for any part that matches a search term
  const tokens = findTerms(filter).filter((token) => token.parseType === PARSE_TYPE.TERM);
  const terms: Array<Term> = tokens as Array<Term>;
  for (let i = 0; i < terms.length; i += 1) {
    const term = terms[i];
    let termField = term.field;
    let operator = term.operator;
    let value = term.value;
    if (term.type === TOKEN_TYPE.STRING) {
      // term is just a bare string, defaults to looking in message field
      termField = K.FIELD.MESSAGE;
      operator = 'contains';
      if (term.tokens && term.tokens[0] && term.tokens[0].type === 'regex') {
        // bare string regex, eg: $"/images/[\w\/]+\.png"
        operator = 'matches';
        value = value.substring(1);
      }
      value = unquote(value);
    } else if (term.tokens[2] && term.tokens[2].type === TOKEN_TYPE.STRING) {
      value = unquote(value);
    }
    termField = stripDollar(termField); // normalize to field without leading $ (that's how log line data does it)

    // if we're looking at the right term, perform match based on operator
    if (termField === field && typeof value === 'string' && value.length > 0) {
      const valueLc = value.toLowerCase();
      if (operator === 'contains' && text.toLowerCase().includes(valueLc)) {
        const valueEsc = regExEscape(valueLc);
        const regex = new RegExp('(' + valueEsc + ')', 'gi');
        let m;
        while ((m = regex.exec(text)) !== null) {
          ranges.push({ min: m.index, max: m.index + valueLc.length, startTag, endTag, priority: 1 });
        }
      } else if ((operator === '=' || operator === '==') && text.toLowerCase() === valueLc) {
        ranges.push({ min: 0, max: valueLc.length, startTag, endTag, priority: 1 });
      } else if (operator === 'matches') {
        let regex;
        try {
          // don't escape regex characters here since we want them to retain their meaning; user can include parens but they must match
          regex = new RegExp('(' + valueLc + ')', 'gi');
        } catch (e) {
          // ignore exception
        }
        if (regex) {
          let m;
          const numRanges = ranges.length;
          while ((m = regex.exec(text)) !== null) {
            ranges.push({ min: m.index, max: m.index + m[0].length, startTag, endTag, priority: 1 });
            if (ranges.length > numRanges + MAX_RANGES) {
              ranges.length = numRanges;
              break;
            }
          }
        }
      } else if (operator === 'in' && term.tokens && term.tokens[2] && term.tokens[2].type === EXPRESSION_TYPE.LIST) {
        const listExp: Expression = term.tokens[2] as Expression;
        const values = listExp.tokens
          .filter((token) => token.type === TOKEN_TYPE.STRING)
          .map((token) => String(token.text));
        for (let j = 0; j < values.length; j += 1) {
          const valueLc = unquote(values[j].toLowerCase());
          if (text.toLowerCase() === valueLc) {
            ranges.push({ min: 0, max: text.length, startTag, endTag, priority: 1 });
          }
        }
      }
    }
  }

  return ranges;
};

// local type so we can optimize by compiling each config's regex once
type LinkConfigWithRegex = {
  config: LinkConfig;
  regex: RegExp;
};

/**
 * Insert links into the given text for the given field, based on user configuration. Each link
 * config object can have the following fields:
 *
 *     regex         a JS regex to match the text against (can interpolate matched text from regex)
 *     url           the href for the link if the text matches
 *     searchFields  a list of fields to match against; defaults to ['message']; '*' is all fields
 *     linkTextArg   the numbered argument from the regex to use as the link text
 *     caseSensitive if true, do case-sensitive matching (defaults to false)
 *     subs          substitutions to perform on content of captured text, for
 *                   example: { arg: 1, match: '\.', replace: '/' }
 *
 * As an example, here is a config for Scalyr stacktrace linking:
 *
 *  searchResultLinks: [
 *    {
 *      regex: "com\\.scalyr\\.([a-z\\.\\d]+)[\\w\\.\\$]+\\(((\\w+\\.java):(\\d+))\\)",
 *      subs: [
 *        {arg: 1, match: "\\.", replace: "/"}
 *      ],
 *      url: "https://github.com/scalyr/scalyr/blob/master/ScalyrSite/src/com/scalyr/${1}${3}#L${4}",
 *      linkTextArg: 2,
 *      caseSensitive: true
 *    }
 *  ]
 *
 * Note that regex capture groups (surrounded by parentheses) are used to pull out parts of the
 * text for use in the URL.
 *
 * Matching is done globally, so multiple matches in the same text will all be turned into links.
 * Multiple configs can be provided. The first one that generates a match wins; subsequent configs
 * are ignored.
 *
 * Example before and after for a line from a stacktrace:
 *
 *    com.scalyr.server.GlobalContextListener.runShutdownBlocker(GlobalContextListener.java:128)
 *
 *    com.scalyr.server.GlobalContextListener.runShutdownBlocker(<a href="https://github.com/scalyr/scalyr/blob/master/ScalyrSite/src/com/scalyr/server/GlobalContextListener.java#L85">GlobalContextListener.java:128</a>)
 *
 * @param {string} field         name of the field (facet) to process
 * @param {string} value         text to possibly add links to
 * @param {object} args          args for transform, will have user's SmartLinks config (searchResultLinks in /scalyr/logs)
 *
 * @return list of MarkupRange with info about changes to make
 */
export const linkify = (field: string, value: string, args?: TransformArgs): MarkupRange[] => {
  if (!value) {
    return [];
  }

  const linkConfig = args?.linkConfig;
  if (!linkConfig) {
    return [];
  }

  // replace ${N} with the value of argument N
  const interpolate = (str: string, args: Array<string>): string => {
    for (let i = 1; i < args.length; i += 1) {
      str = str.replace('${' + i + '}', args[i]);
    }
    return str;
  };

  // filter config objects down to the ones we can use
  const goodConfig: Array<LinkConfigWithRegex> = [];
  linkConfig.forEach((config) => {
    // check whether this config applies to the given field; if it doesn't, skip this config
    const fields = config.searchFields && config.searchFields.length > 0 ? config.searchFields : [K.FIELD.MESSAGE];
    if (fields.includes(field) || fields === '*') {
      // construct the regex; if it's invalid, skip this config
      let compiledRegex;
      try {
        compiledRegex = new RegExp(config.regex, config.caseSensitive ? '' : 'i');
      } catch (e) {
        // this config will not be used
      }
      if (compiledRegex) {
        const configCopy = cloneDeep(config);
        goodConfig.push({
          config: configCopy,
          regex: compiledRegex,
        });
      }
    }
  });

  const lines = value.split(K.REGEX.LINE_RETURN);
  const ranges: MarkupRange[] = [];
  const endTag = '</a>';
  let totalLength = 0;

  // loop through text one line at a time finding matches and patching in links
  lines.forEach((line) => {
    // Iterate all configs and apply only the first matching.
    for (let j = 0; j < goodConfig.length; j += 1) {
      const configObject = goodConfig[j];
      const config = configObject.config;
      const match = configObject.regex.exec(line);
      if (match) {
        const args = match.slice(0);
        if (config.subs && config.subs.length > 0) {
          for (let i = 0; i < config.subs.length; i += 1) {
            const sub = config.subs[i];
            const subRe = new RegExp(sub.match, 'g');
            // TODO: do we want to allow interpolation on replacement string?
            args[sub.arg] = args[sub.arg].replace(subRe, sub.replace);
          }
        }
        const newUrl = interpolate(config.url, args);
        const startTag = `<a target="_blank" class="link" href="${newUrl}">`;

        // construct the link by doing pre + link + post

        // link text
        const fullMatch = match[0];
        // custom link text from a captured group
        const linkText = config.linkTextArg && config.linkTextArg > 0 && match[config.linkTextArg];
        const linkTextMatch = linkText && fullMatch.match(linkText);
        if (linkText && linkTextMatch && linkTextMatch.index !== undefined) {
          const startPos = totalLength + match.index + linkTextMatch.index;
          const endPos = totalLength + match.index + linkTextMatch.index + linkText.length;
          ranges.push({ min: startPos, max: endPos, startTag, endTag, priority: 2 });
        } else {
          // default link text is the full matched text
          const startPos = totalLength + match.index;
          const endPos = totalLength + match.index + fullMatch.length;
          ranges.push({ min: startPos, max: endPos, startTag, endTag, priority: 2 });
        }

        break; // first matching link config wins
      }
    }
    totalLength += line.length + 1;
  });

  return ranges;
};

/**
 * Reduce the first pair of nested or overlapping ranges with the same tags to a single range. Ranges must already be sorted.
 *
 * @param ranges    list of MarkupRange
 * @returns list of MarkupRange
 */
export const collapseRanges = (ranges: MarkupRange[]): MarkupRange[] => {
  let newRanges: MarkupRange[] = [];
  for (let i = 0; i < ranges.length; i += 1) {
    const range = ranges[i];
    const nextRange = ranges[i + 1];
    const tagsMatch = nextRange && range.startTag === nextRange.startTag && range.endTag === nextRange.endTag;
    if (tagsMatch && nextRange.min >= range.min && nextRange.min <= range.max) {
      const newRange = cloneDeep(range);
      if (nextRange.max > range.max) {
        newRange.max = nextRange.max;
      }
      newRanges = newRanges.concat(newRange, ranges.slice(i + 2));
      break;
    } else {
      newRanges.push(range);
    }
  }

  return newRanges;
};

/**
 * Convert the first pair of overlapping ranges with different tags to a trio of non-overlapping ranges, by breaking up the range
 * with the lower priority. Ranges must already be sorted.
 *
 * @param ranges    list of MarkupRange
 * @returns list of MarkupRange
 */
export const resolveRanges = (ranges: MarkupRange[]): MarkupRange[] => {
  let newRanges: MarkupRange[] = [];
  for (let i = 0; i < ranges.length; i += 1) {
    const range = ranges[i];
    const nextRange = ranges[i + 1];
    if (nextRange && nextRange.min > range.min && nextRange.min < range.max && nextRange.max > range.max) {
      if (range.priority <= nextRange.priority) {
        const newRange1 = cloneDeep(range);
        const newRange2 = cloneDeep(range);
        newRange1.max = nextRange.min;
        newRange2.min = nextRange.min;
        newRanges.push(newRange1, nextRange, newRange2);
      } else {
        const newRange1 = cloneDeep(nextRange);
        const newRange2 = cloneDeep(nextRange);
        newRange1.max = range.max;
        newRange2.min = range.max;
        newRanges.push(range, newRange1, newRange2);
      }
      newRanges = newRanges.concat(ranges.slice(i + 2));
      break;
    } else {
      newRanges.push(range);
    }
  }

  return newRanges;
};

/**
 * Take a list of ranges with instructions about how to mark up the given text, and apply them. Each range has start and
 * end points, and start and end tags. If two ranges overlap, the one with the lower priority is broken into two sets of
 * tags.
 *
 * @param text      text to mark up
 * @param ranges    list of MarkupRange
 * @returns valid HTML with no overlapping tags
 */
export const processRanges = (text: string, ranges: MarkupRange[]): string => {
  if (!ranges || ranges.length === 0) {
    return escape(text);
  }

  const sortedRanges = ranges.sort((rangeA: MarkupRange, rangeB: MarkupRange) => {
    if (rangeA.min === rangeB.min) {
      if (rangeA.max === rangeB.max) {
        return 0;
      }
      return rangeA.max > rangeB.max ? -1 : 1;
    }
    return rangeA.min < rangeB.min ? -1 : 1;
  });

  // run a transforming function on a list of ranges until no more changes are made
  const runUntilDone = (ranges: MarkupRange[], rangeFunc: (ranges: MarkupRange[]) => MarkupRange[]): MarkupRange[] => {
    let sourceRanges = cloneDeep(ranges);
    let newRanges: MarkupRange[] = [];
    let done = false;
    while (!done) {
      newRanges = rangeFunc(sourceRanges);
      done = newRanges.length === sourceRanges.length;
      sourceRanges = newRanges;
    }
    return sourceRanges;
  };

  const collapsedRanges = runUntilDone(sortedRanges, collapseRanges);
  const resolvedRanges = runUntilDone(collapsedRanges, resolveRanges);

  let output = '';
  for (let i = 0; i <= text.length; i += 1) {
    const minRanges = resolvedRanges.filter((range) => range.min === i);
    for (let r = 0; r < minRanges.length; r += 1) {
      output += minRanges[r].startTag;
    }
    // since end of a range is one past last character (like substring), we add closing tag before character
    const maxRanges = resolvedRanges.filter((range) => range.max === i);
    for (let r = maxRanges.length - 1; r >= 0; r -= 1) {
      output += maxRanges[r].endTag;
    }
    if (i < text.length) {
      output += escape(text[i]);
    }
  }

  return output;
};

/**
 * Take a list of transforms and apply them to the given text.
 *
 * @param field         the name of a facet
 * @param text          facet value
 * @param transforms    a list of transform types
 *
 * @returns HTML
 */
export const applyTransforms = (
  field: string,
  text: string,
  transforms: TRANSFORM_TYPE[],
  transformArgs: TransformArgs,
): string => {
  let ranges: MarkupRange[] = [];
  for (let i = 0; i < transforms.length; i++) {
    if (transforms[i] === TRANSFORM_TYPE.HIGHLIGHT) {
      ranges = ranges.concat(showMatches(field, text, transformArgs));
    } else if (transforms[i] === TRANSFORM_TYPE.LINKIFY) {
      ranges = ranges.concat(linkify(field, text, transformArgs));
    }
  }

  return processRanges(text, ranges);
};

/**
 * Take a string of JSON and return a pretty-printed form of it. Keys and values
 * by default are wrapped in SPANs with classes. Values of an object are aligned.
 * Handles JSON objects and arrays.
 *
 * @param str           a string of possible JSON
 * @param indent        how many spaces to indent each level (defaults to 2)
 *
 * @returns pretty-printed JSON if the string was parsable JSON; the original string otherwise
 */
export const prettyPrint = (
  str: string,
  field?: string,
  transforms?: TRANSFORM_TYPE[],
  transformArgs?: TransformArgs,
  indent = 2,
): string => {
  if (!looksLikeJson(str)) {
    return escape(str);
  }

  // Wrap text in a SPAN with a class.
  // Note: this is an HTML string and not JSX, so it uses "class" rather than "className".
  const wrap = (text: string, className: string): string => {
    let result = '<span class="' + className + '">';
    result +=
      field && transforms && transformArgs ? applyTransforms(field, text, transforms, transformArgs) : escape(text);
    result += '</span>';
    return result;
  };

  // format the given value based on its type
  const pretty = (value: any, level: number): string => {
    if (isString(value)) {
      return wrap(quote(value), 'json-value');
    } else if (isNumber(value) || isBoolean(value) || isNull(value)) {
      return wrap(String(value), 'json-value');
    } else if (isArray(value)) {
      if (value.length === 0) {
        return '[]\n';
      }
      let buffer = '[\n';
      const space = ' '.repeat(level * indent);
      value.forEach((val) => (buffer += space + pretty(val, level + 1) + ',\n'));
      buffer += ' '.repeat((level - 1) * indent) + ']';
      return buffer;
    } else if (isObjectLike(value)) {
      const keys = Object.keys(value);
      const maxLength = getMaxLength(keys);
      let buffer = '{\n';
      const space = ' '.repeat(level * indent);
      keys.forEach((key) => {
        const val = value[key];
        let valuePad = '';
        if (!isArray(val) && !isObjectLike(val)) {
          valuePad = ' '.repeat(maxLength - key.length);
        }
        buffer += space + wrap(key, 'json-key') + ': ' + valuePad + pretty(val, level + 1) + ',\n';
      });
      buffer += ' '.repeat((level - 1) * indent) + '}';
      return buffer;
    }

    return '';
  };

  // First try JSON.parse, then fall back to partialParse (which has been seen to fail on parseable content - see FRONT 7311)
  const doParse = (text: string) => {
    let result;
    try {
      result = JSON.parse(text);
    } catch (e) {
      try {
        result = partialParse(text);
      } catch (e) {}
    }
    return result;
  };

  // escape special characters that aren't valid JSON (there are more, these are the main ones)
  const escapedStr = str.replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/\t/g, '\\t');
  let parsed = doParse(escapedStr);
  if (!parsed && escapedStr.startsWith('[') && escapedStr.endsWith(']')) {
    // be lenient with arrays of unquoted strings; quote them and try parsing the result
    const newStr =
      '[' +
      escapedStr
        .substring(1, escapedStr.length - 1)
        .split(',')
        .map((part) => quote(part.trim(), true))
        .join(',') +
      ']';
    parsed = doParse(newStr);
  }

  return parsed ? pretty(parsed, 1) : escape(str);
};

/**
 * Mark up a facet value in one or more ways: pretty-print it (JSON), highlight matches based on the filter, and/or
 * add Smart Links.
 *
 * @param field         the name of a facet
 * @param text          facet value
 * @param transforms    a list of transform types
 *
 * @returns HTML
 */
export const transformContent = (
  field: string,
  text: string,
  transforms: TRANSFORM_TYPE[],
  transformArgs: TransformArgs,
): string => {
  const unquoted = unquote(text);
  const isQuoted = unquoted.length !== text.length;

  let result = text;
  if (transforms.includes(TRANSFORM_TYPE.PRETTY_PRINT) && looksLikeJson(unquoted)) {
    result = prettyPrint(unquoted, field, transforms, transformArgs);
  } else {
    result = applyTransforms(field, unquoted, transforms, transformArgs);
  }

  return isQuoted ? quote(result) : result;
};

/**
 * Getting number of bytes and transforming it to string with KB's suffix
 *
 * @param bytes - number of bytes
 */
// TODO: Needed? Can we use `formatBytes` from `src/components/billingAndUsage/utils.ts` instead?
export const convertBytesToKilobyteStr = (bytes: number) => `${(bytes / 1000).toFixed(2)}KB`;
